import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { OrderDistanceShared } from 'src/app/components/shared/models/interface/orderDistance.interface';
import { MuMapComponent } from 'src/app/components/shared/sharedModules/designSystem/mu-map/mu-map.component';
import { Flags } from 'src/app/components/shared/types/flags.type';
import { LayoutContentColumnSizeShared } from '../../models/interface/layoutContentColumnSize.interface';
import { LayoutContentTextsShared } from '../../models/interface/layoutContentTexts.interface';
import { LayoutMapFragmentContentShared } from '../../models/interface/layoutMapFragment.interface';
import { LayoutPageAndMapSettingsShared } from '../../models/interface/layoutPageAndMapSettings.interface';
import { MapElementShared } from '../../models/interface/mapElement.interface';
import { MapOptionsShared } from '../../models/interface/mapOptions.interface';
import { SimplyCoordinatesShared } from '../../models/interface/simplyCoordinates.interface';
import { AtlasType } from '../../sharedModules/designSystem/mu-atlas/interfaces/mu-atlas-types.interface';

@Injectable({
  providedIn: 'root',
})
export class MuLayoutPageAndMapService {
  private defaultSettings: LayoutPageAndMapSettingsShared = {
    overflowContent: false,
    buttonDisabled: true,
    columnSize: {
      startColumn: 6,
      endColumn: 6,
    },
  };
  private mobileMapPlaceHolderSubject = new BehaviorSubject<string>(null);
  private mobileMapPlaceHolder$ = this.mobileMapPlaceHolderSubject.asObservable();
  private mapOptionsSubject = new Subject<MapOptionsShared>();
  private mapOptions$ = this.mapOptionsSubject.asObservable();
  private columnSizeSubject = new Subject<LayoutContentColumnSizeShared>();
  private columnSize$ = this.columnSizeSubject.asObservable();
  private textsSubject = new BehaviorSubject<LayoutContentTextsShared>(null);
  private texts$ = this.textsSubject.asObservable();
  private makersSubject = new Subject<MapElementShared[]>();
  private markers$ = this.makersSubject.asObservable();
  private markersPositionOnlySubject = new Subject<MapElementShared[]>();
  private markersPositionOnly$ = this.markersPositionOnlySubject.asObservable();
  private readyMapSubject = new BehaviorSubject<MuMapComponent<any>>(null);
  private readyMap$ = this.readyMapSubject.asObservable().pipe(filter(value => (value ? true : false)));
  private buttonFlagListSubject = new Subject<Flags<any>>();
  private buttonFlagList$ = this.buttonFlagListSubject.asObservable();
  private buttonFlagSubject = new Subject<any>();
  private buttonFlag$ = this.buttonFlagSubject.asObservable();
  private buttonDisabledSubject = new Subject<boolean>();
  private buttonDisabled$ = this.buttonDisabledSubject.asObservable();
  private mapFragmentSubject = new BehaviorSubject<LayoutMapFragmentContentShared>(null);
  private mapFragment$ = this.mapFragmentSubject.asObservable();
  private orderDistanceSubject: BehaviorSubject<OrderDistanceShared> = new BehaviorSubject<OrderDistanceShared>(null);
  private orderDistance$: Observable<OrderDistanceShared> = this.orderDistanceSubject.asObservable();
  private showOrderDistanceSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  private showOrderDistance$: Observable<boolean> = this.showOrderDistanceSubject.asObservable();
  private isDragSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  private isDrag$: Observable<boolean> = this.isDragSubject.asObservable();
  private fitBoundsSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  private fitBounds$: Observable<boolean> = this.fitBoundsSubject.asObservable();
  private currentLocationSubject = new BehaviorSubject<SimplyCoordinatesShared>(null);
  private currentLocation$ = this.currentLocationSubject.asObservable();
  private askForBrowserLocationSubject = new BehaviorSubject<boolean>(null);
  private askForBrowserLocation$ = this.askForBrowserLocationSubject.asObservable();
  useOverflow = false;
  private settings: LayoutPageAndMapSettingsShared = this.defaultSettings;
  private atlasTypeSubject = new BehaviorSubject<AtlasType>(AtlasType.GOOGLE);
  private atlasType$ = this.atlasTypeSubject.asObservable();

  constructor() {
    this.setAskForBrowserLocation(true);
  }

  /**
   * get observable of MapPlaceHolder
   */
  get mobileMapPlaceHolder(): Observable<string> {
    return this.mobileMapPlaceHolder$;
  }

  /**
   * set map options
   * @param {MapOptions} mapOptions
   */
  setMapOptions(mapOptions: MapOptionsShared): void {
    this.mapOptionsSubject.next(mapOptions);
  }

  /**
   * get observable of map options
   * @return {Observable<ElementRef>} response
   */
  get mapOptions(): Observable<MapOptionsShared> {
    return this.mapOptions$;
  }

  /**
   * get column size observable
   */
  get columnSize(): Observable<LayoutContentColumnSizeShared> {
    return this.columnSize$;
  }

  /**
   * get texts observable
   */
  get texts(): Observable<LayoutContentTextsShared> {
    return this.texts$;
  }

  /**
   * set markers
   * @param markers
   */
  setMarkers(markers: MapElementShared[]): void {
    this.makersSubject.next(markers);
  }

  /**
   * get markers
   */
  get markers(): Observable<MapElementShared[]> {
    return this.markers$;
  }

  /**
   * set markers without triggering viewport fitting
   * @param markers
   */
  setMarkersPositionOnly(markers: MapElementShared[]): void {
    this.markersPositionOnlySubject.next(markers);
  }

  /**
   * get position-only markers observable
   */
  get markersPositionOnly(): Observable<MapElementShared[]> {
    return this.markersPositionOnly$;
  }

  /**
   * set ready map
   * @param readyMap
   */
  setReadyMap(map: MuMapComponent<any>): void {
    this.readyMapSubject.next(map);
  }

  /**
   * get ready map observable
   */
  get readyMap(): Observable<MuMapComponent<any>> {
    return this.readyMap$;
  }

  /**
   * get button flag list
   * @return response
   */
  get buttonFlagList(): any {
    return this.buttonFlagList$;
  }

  /**
   * get button flag list
   * @return response
   */
  get buttonFlag(): any {
    return this.buttonFlag$;
  }

  /**
   * bet button disabled value
   * @return {Observable<boolean>} response
   */
  get buttonDisabled(): Observable<boolean> {
    return this.buttonDisabled$;
  }

  /**
   * get order distance
   */
  get orderDistance(): Observable<OrderDistanceShared> {
    return this.orderDistance$;
  }

  setShowOrderDistance(show: boolean): void {
    this.showOrderDistanceSubject.next(show);
  }

  get showOrderDistance(): Observable<boolean> {
    return this.showOrderDistance$;
  }

  /**
   * get map current settings
   */
  get currentSettings(): LayoutPageAndMapSettingsShared {
    return this.cloneSettings(this.settings);
  }

  /**
   * return map fragment
   */
  get mapFragment(): Observable<LayoutMapFragmentContentShared> {
    return this.mapFragment$;
  }

  /**
   *
   * @param {boolean} value
   */
  setIsDrag(value: boolean): void {
    this.isDragSubject.next(value);
  }

  /**
   * is drag active
   */
  get isDrag(): Observable<boolean> {
    return this.isDrag$;
  }

  /**
   * set fit bounds
   * @param {boolean} value
   */
  setFitBounds(value: boolean): void {
    this.fitBoundsSubject.next(value);
  }

  /**
   * get fit bounds
   * @returns {Observable<boolean>} response
   */
  get fitBounds(): Observable<boolean> {
    return this.fitBounds$;
  }

  /**
   * set AskForBrowserLocation
   * @param {boolean} value
   */
  setAskForBrowserLocation(value: boolean): void {
    this.askForBrowserLocationSubject.next(value);
  }

  /**
   * get AskForBrowserLocation
   * @returns {Observable<boolean>} response
   */
  get askForBrowserLocation(): Observable<boolean> {
    return this.askForBrowserLocation$;
  }

  private cloneSettings(settings: LayoutPageAndMapSettingsShared): LayoutPageAndMapSettingsShared {
    const result = {
      ...settings,
    };
    if (settings.columnSize) {
      result.columnSize = { ...settings.columnSize };
    }
    if (settings.texts) {
      result.texts = { ...settings.texts };
    }
    if (settings.mapFragment) {
      result.mapFragment = { ...settings.mapFragment };
    }
    return result;
  }

  /**
   *
   * @param settings configure layout map
   */
  setup(settings: LayoutPageAndMapSettingsShared) {
    const finalSettings: LayoutPageAndMapSettingsShared = this.cloneSettings({
      ...this.defaultSettings,
      ...settings,
    });
    this.settings = finalSettings;
    this.buttonFlagListSubject.next(settings.buttonFlagList);
    this.buttonFlagSubject.next(settings.buttonFlag);
    this.useOverflow = finalSettings.overflowContent;
    this.buttonDisabledSubject.next(finalSettings.buttonDisabled);
    if (finalSettings.texts) {
      this.textsSubject.next(finalSettings.texts);
    }
    if (finalSettings.columnSize) {
      this.columnSizeSubject.next(finalSettings.columnSize);
    }
    if (finalSettings.orderDistance) {
      this.orderDistanceSubject.next(finalSettings.orderDistance);
    }
    this.mobileMapPlaceHolderSubject.next(finalSettings.mobilePlaceHolder);
    this.mapFragmentSubject.next(finalSettings.mapFragment);
  }

  /** reset configuration to default values */
  reset() {
    this.setup({});
  }

  /**
   *
   * @param settings
   */
  updateSettings(settings: LayoutPageAndMapSettingsShared) {
    this.setup({ ...this.settings, ...settings });
  }

  /**
   * Emit a notification for observers with the location getting from browser.
   * The notification has latitude and longitude if user allows location permissions;
   * other case emit null
   * @param location Object representing Latitude and Longitude
   */
  setCurrentAddress(location: SimplyCoordinatesShared) {
    this.currentLocationSubject.next(location);
  }

  /**
   * Get observable for current browser location
   */
  get currentLocation(): Observable<SimplyCoordinatesShared> {
    return this.currentLocation$;
  }

  /**
   * get atlas type
   */
  get atlasType(): Observable<AtlasType> {
    return this.atlasType$;
  }

  /**
   * set atlas type
   * @param {AtlasType} atlasType
   */
  setAtlasType(atlasType: AtlasType) {
    this.atlasTypeSubject.next(atlasType);
  }
}
